// src/hooks/useSession.ts - Simplified to only handle batchId
import { useState, useEffect } from 'react';

interface SessionData {
  batchId: string | undefined;  // Changed from null to undefined
  isSessionActive: boolean;
}

export const useSession = (): SessionData => {
  const [sessionData, setSessionData] = useState<SessionData>({
    batchId: undefined,  // Changed from null to undefined
    isSessionActive: false
  });

  useEffect(() => {
    const loadSessionData = () => {
      const batchId = localStorage.getItem('scannerBatchId') || undefined;  // Convert null to undefined
      const isSessionActive = !!batchId;
      
      setSessionData({
        batchId,
        isSessionActive
      });
    };

    // Load initial data
    loadSessionData();

    // Listen for storage changes (if session is updated in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'scannerBatchId') {
        loadSessionData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return sessionData;
};

// Helper function to clear session data
export const clearSession = () => {
  localStorage.removeItem('scannerBatchId');
};

// Helper function to extract name from batchId
export const extractNameFromBatchId = (batchId: string | undefined): string => {
  if (!batchId) return '';
  const parts = batchId.split('_');
  return parts[0] || '';
};

// Helper function to extract readable date from batchId
export const extractDateTimeFromBatchId = (batchId: string | undefined): { date: string; time: string } => {
  if (!batchId) return { date: '', time: '' };
  
  const parts = batchId.split('_');
  if (parts.length < 3) return { date: '', time: '' };
  
  const datePart = parts[1]; // YYYYMMDD
  const timePart = parts[2]; // HHMMSS
  
  if (datePart.length === 8 && timePart.length >= 6) {
    const year = datePart.substring(0, 4);
    const month = datePart.substring(4, 6);
    const day = datePart.substring(6, 8);
    
    const hours = timePart.substring(0, 2);
    const minutes = timePart.substring(2, 4);
    const seconds = timePart.substring(4, 6);
    
    const date = `${month}/${day}/${year}`;
    const time = `${hours}:${minutes}:${seconds}`;
    
    return { date, time };
  }
  
  return { date: '', time: '' };
};

// Helper function to format batchId timestamp (readable)
export const formatBatchTime = (batchId: string | undefined): string => {
  const { date, time } = extractDateTimeFromBatchId(batchId);
  if (!date || !time) return '';
  
  return `${date}, ${time}`;
};

// Legacy function for backward compatibility (if needed)
export const extractTimestampFromBatchId = (batchId: string | undefined): number => {
  const { date, time } = extractDateTimeFromBatchId(batchId);
  if (!date || !time) return 0;
  
  // Convert back to timestamp if needed
  const dateTime = new Date(`${date} ${time}`);
  return dateTime.getTime();
};